import TPEAction from "src/models/common/TPEAction";
import {MultidimensionalAllocationDataSourcesState} from "./MultidimensionalAllocationDataSourcesState";
import {
    getNewMultiAllocationDataSourceRecord,
    MultiAllocationDataSourceRecord
} from "src/models/multidimensional-allocation/MultiAllocationDataSourceRecord";
import {MultidimensionalAllocationLOV} from "src/models/multidimensional-allocation/MultidimensionalAllocationLOV";
import CONSTANTS from "src/utils/constants";

export const MULTI_ALLOCATION_DATA_SOURCE_ACTIONS = Object.freeze({
    SET_RECORDS: new TPEAction("SET_RECORDS"),
    ADD_NEW_RECORD: new TPEAction("ADD_NEW_RECORD"),
    EDIT_RECORD: new TPEAction("EDIT_RECORD"),
    CANCEL_EDITING: new TPEAction("CANCEL_EDITING"),
    SAVE_RECORD: new TPEAction("SAVE_RECORD"),
    DELETE_RECORD: new TPEAction("DELETE_RECORD"),
    SET_CUSTOM_COA_REFERENCE: new TPEAction("SET_CUSTOM_COA_REFERENCE"),
    SET_DATASOURCE_LOV: new TPEAction("SET_DATASOURCE_LOV"),
    TOGGLE_SHOW_DECIMALS: new TPEAction("TOGGLE_SHOW_DECIMALS"),
    UPDATE_RECORD_FIELD: new TPEAction("UPDATE_RECORD_FIELD"),
    SHOW_DATA_KEY_SELECTION: new TPEAction("SHOW_DATA_KEY_SELECTION"),
    TOGGLE_DATA_KEY_VIEW: new TPEAction("TOGGLE_DATA_KEY_VIEW"),
    HIDE_EDIT_CONFIRM_MODAL: new TPEAction("HIDE_EDIT_CONFIRM_MODAL"),
    SET_DATA_KEY_COA_INPUT: new TPEAction("SET_DATA_KEY_COA_INPUT"),
    REMOVE_DATA_KEY_COA_INPUT: new TPEAction("REMOVE_DATA_KEY_COA_INPUT"),
    REMOVE_UNUSED_DATA_KEY_COA_INPUTS: new TPEAction("REMOVE_UNUSED_DATA_KEY_COA_INPUTS"),
    SET_ERRORS: new TPEAction("SET_ERRORS"),
    CLEAR_ERRORS: new TPEAction("CLEAR_ERRORS"),
    SET_FX_INPUTS: new TPEAction("SET_FX_INPUTS"),
    SET_DATA_KEY_INPUT: new TPEAction('SET_DATA_KEY_INPUT'),
    SET_RECORD_ID_FOR_ACTION: new TPEAction('SET_RECORD_ID_FOR_ACTION'),
    SHOW_EDIT_CONFIRM_MODAL: new TPEAction('SHOW_EDIT_CONFIRM_MODAL'),
    SHOW_DELETE_CONFIRM_MODAL: new TPEAction('SHOW_DELETE_CONFIRM_MODAL'),
    SHOW_DUPLICATE_CONFIRM_MODAL: new TPEAction('SHOW_DUPLICATE_CONFIRM_MODAL'),
    DUPLICATE_RECORD: new TPEAction('DUPLICATE_RECORD'),
    HIDE_DUPLICATE_CONFIRM_MODAL: new TPEAction('HIDE_DUPLICATE_CONFIRM_MODAL'),
    HIDE_DELETE_CONFIRM_MODAL: new TPEAction('HIDE_DELETE_CONFIRM_MODAL'),
    TOGGLE_CATEGORY_EXPANSION: new TPEAction("TOGGLE_CATEGORY_EXPANSION"),
    UPDATE_BALANCE_PULL_STATUS: new TPEAction('UPDATE_BALANCE_PULL_STATUS'),
    UPDATE_LAST_BALANCE_PULL_ID: new TPEAction('UPDATE_LAST_BALANCE_PULL_ID'),
    REFRESH_BALANCE_STATUS: new TPEAction('REFRESH_BALANCE_STATUS'),
    SET_ALLOCATION_SHEETS: new TPEAction('SET_ALLOCATION_SHEETS'),
    SET_ALLOCATION_SHEETS_LOADING: new TPEAction('SET_ALLOCATION_SHEETS_LOADING'),
    FILTER_ALLOCATION_SHEETS_BY_SEGMENT: new TPEAction('FILTER_ALLOCATION_SHEETS_BY_SEGMENT'),
    SET_SELECTED_CUSTOM_COA: new TPEAction('SET_SELECTED_CUSTOM_COA'),
});

export function multidimensionalAllocationDataSourcesReducer(
    state: MultidimensionalAllocationDataSourcesState,
    action: TPEAction
): MultidimensionalAllocationDataSourcesState {

    switch (action.type) {
        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_RECORDS.type:
            const dataRecords = action.payload as MultiAllocationDataSourceRecord[];
            // Sort records by created timestamp in ascending order
            const sortedRecords = [...dataRecords].sort((a, b) => (a.createdDate || 0) - (b.createdDate || 0));
            return {
                ...state,
                datasetRecords: sortedRecords
            };

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.ADD_NEW_RECORD.type:
            const newRecord = getNewMultiAllocationDataSourceRecord(state.recordIdCounter);
            newRecord.unsavedDatasourceId = newRecord.datasourceId;
            newRecord.worksheetId = action.payload.worksheetId
            newRecord.worksheetVersion = action.payload.worksheetVersion
            newRecord.worksheetName = action.payload.worksheetName
            return {
                ...state,
                loading: false,
                datasetRecords: [...state.datasetRecords, newRecord],
                recordIdCounter: state.recordIdCounter + 1,
                recordBeingEdited: newRecord, // New record is in edit mode
                savedRecordId: undefined,
            };

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.EDIT_RECORD.type: {
            const dataSourceRecords = [...state.datasetRecords];
            const selectedRecordArr = dataSourceRecords.filter(x => x.datasourceId == state.recordIdForAction);
            if (selectedRecordArr.length > 0) {
                const selectedRecord = selectedRecordArr[0];
                const selectedRecordCopy = {
                    ...selectedRecord,
                    balancePullInProgress: false,
                    dataKeyInput: selectedRecord.dataKeyInput ? {
                        ...selectedRecord.dataKeyInput,
                        selectedCOA: new Map(selectedRecord.dataKeyInput.selectedCOA)
                    } : undefined
                };
                selectedRecord.isEditing = true;
                selectedRecord.showDataKeySelection = true;
                return {
                    ...state,
                    recordBeingEdited: selectedRecord,
                    datasetRecords: dataSourceRecords,
                    recordBeingEditedOriginal: selectedRecordCopy,
                    savedRecordId: undefined,
                }
            } else {
                return state;
            }
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.CANCEL_EDITING.type: {
            const editingRecord = state.recordBeingEdited;

            if (editingRecord == null) {
                return {...state};
            }

            if (editingRecord?.isNewRecord) {
                // Delete the record if it is a new record
                const records = state.datasetRecords.filter(x => x.datasourceId != editingRecord.datasourceId);
                return {
                    ...state,
                    datasetRecords: records,
                    recordBeingEdited: undefined,
                    recordBeingEditedOriginal: undefined
                }
            } else {
                // Restore the original record for existing records
                const index = state.datasetRecords.indexOf(editingRecord || {} as MultiAllocationDataSourceRecord);
                // Nothing to restore keeps the same list, so closing a viewed data source does not re-publish the records.
                let records = state.datasetRecords;
                if (index >= 0 && state.recordBeingEditedOriginal != null) {
                    records = [...state.datasetRecords];
                    records[index] = state.recordBeingEditedOriginal;
                }
                return {
                    ...state,
                    recordBeingEdited: undefined,
                    recordBeingEditedOriginal: undefined,
                    datasetRecords: records,
                }
            }
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SAVE_RECORD.type:
            const {recordBeingEdited} = state;

            // This is the object coming from the web API response
            const {recordID, updatedRecord} = action.payload;

            const records = [...state.datasetRecords];
            const {selectedRecord} = getSelectedRecordAndParams(action.payload, records);
            if (selectedRecord != null) {
                selectedRecord.datasourceId = updatedRecord.datasourceId;
                selectedRecord.currency = updatedRecord.currency;
                selectedRecord.allocationSegment = updatedRecord.allocationSegment;
                selectedRecord.totalBalance = updatedRecord.totalBalance;
                selectedRecord.isNewRecord = false;
                selectedRecord.isEditing = false;
                selectedRecord.showDataKeySelection = false;
                selectedRecord.dataKey = ((selectedRecord.dataKeyInput?.selectedCOA.size || 0) + 2) + CONSTANTS.DATA_SOURCE_DATA_KEY_COUNT_SUFFIX; // Add 2 for company and account which are mandatory
                selectedRecord.errors = undefined;

                if (recordBeingEdited != null && recordBeingEdited.datasourceId == selectedRecord.datasourceId) {
                    return {
                        ...state,
                        recordBeingEdited: undefined,
                        recordBeingEditedOriginal: undefined,
                        savedRecordId: selectedRecord.datasourceId,
                        datasetRecords: records,
                    }
                } else {
                    return {
                        ...state,
                        datasetRecords: records,
                    }
                }

            } else {
                return state;
            }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.DELETE_RECORD.type:
            const recordIdToDelete = action.payload as string;
            const filteredRecords = state.datasetRecords.filter(record => record.datasourceId !== recordIdToDelete);

            // Clear recordBeingEdited if we're deleting the record that's currently being edited
            const newRecordBeingEdited = state.recordBeingEdited?.datasourceId === recordIdToDelete
                ? undefined
                : state.recordBeingEdited;

            return {
                ...state,
                datasetRecords: filteredRecords,
                recordBeingEdited: newRecordBeingEdited,
                recordBeingEditedOriginal: newRecordBeingEdited ? state.recordBeingEditedOriginal : undefined
            };

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_CUSTOM_COA_REFERENCE.type:
            return {
                ...state,
                customCoaReference: action.payload as Map<string, string>
            };

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_DATASOURCE_LOV.type:
            return {
                ...state,
                multidimensionalAllocationLOV: action.payload as MultidimensionalAllocationLOV
            };

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.TOGGLE_SHOW_DECIMALS.type:
            return {
                ...state,
                showDecimals: !state.showDecimals
            };

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.UPDATE_RECORD_FIELD.type:
            const editingRecord = state.recordBeingEdited as any;
            const {fieldName, value} = action.payload;

            // Clear the custom period value if a different period option is selected
            // Clear the period value if custom period is updated
            if (fieldName == CONSTANTS.DATA_SOURCE_FIELDS.PERIOD.ACCESSOR && value != CONSTANTS.DATA_SOURCE_VALUES.CUSTOM_PERIOD) {
                editingRecord['customPeriod'] = null;
            } else if (fieldName == CONSTANTS.DATA_SOURCE_FIELDS.CUSTOM_PERIOD.ACCESSOR) {
                editingRecord['period'] = null;
            }

            // LG Code (segment 8) is only valid for Secondary Ledger. When the datasource type
            // changes away from Secondary Ledger, drop any stale LG Code selection so it is not
            // silently persisted on a non-SL record.
            if (fieldName == CONSTANTS.DATA_SOURCE_FIELDS.DATA_SOURCE.ACCESSOR
                    && value != CONSTANTS.DATA_SOURCE_TYPES.SECONDARY_LEDGER) {
                (editingRecord?.dataKeyInput?.selectedCOA as Map<string, string> | undefined)
                    ?.delete(CONSTANTS.COA_SEGMENT_MAPPING.LG_CODE.UI);
            }

            editingRecord[action.payload.fieldName] = action.payload.value;

            return {
                ...state,
                loading: false,
                datasetRecords: [...state.datasetRecords],
            };

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SHOW_DATA_KEY_SELECTION.type: {
            if (state.recordBeingEdited != null) {
                state.recordBeingEdited.showDataKeySelection = true;
            }
            return {
                ...state,
                datasetRecords: [...state.datasetRecords],
            };
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.TOGGLE_DATA_KEY_VIEW.type: {
            const records = [...state.datasetRecords];
            const {selectedRecord} = getSelectedRecordAndParams(action.payload, records);
            if (selectedRecord != null) {
                if (selectedRecord == state.recordBeingEdited) {
                    selectedRecord.showDataKeySelection = false;
                    return {
                        ...state,
                        recordBeingEdited: undefined,
                        datasetRecords: records,
                    }
                } else {
                    if (state.recordBeingEdited != null) {
                        state.recordBeingEdited.showDataKeySelection = false;
                    }
                    selectedRecord.showDataKeySelection = true;
                    return {
                        ...state,
                        recordBeingEdited: selectedRecord,
                        datasetRecords: records,
                    }
                }
            } else {
                return state;
            }
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.HIDE_EDIT_CONFIRM_MODAL.type: {
            return {
                ...state,
                showEditActionConfirmModal: false,
            }
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_DATA_KEY_COA_INPUT.type: {
            const {key, value} = action.payload;
            const editingRecord = state.recordBeingEdited as any;
            if (editingRecord?.dataKeyInput?.selectedCOA) {
                editingRecord.dataKeyInput.selectedCOA.set(key, value);
            }
            return {
                ...state,
                datasetRecords: [...state.datasetRecords],
                selectedCustomCoaValues: undefined,
            };
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_SELECTED_CUSTOM_COA.type: {
            const { valueMap, referenceMap } = action.payload;
            const existingReferenceMap = state.customCoaReference || new Map<string, string>();
            (referenceMap as Map<string, string>).forEach((value: string, key: string) => existingReferenceMap.set(key, value));

            return {
                ...state,
                selectedCustomCoaValues: valueMap,
                customCoaReference: existingReferenceMap,
            };
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.REMOVE_DATA_KEY_COA_INPUT.type: {
            const key = action.payload;
            const editingRecord = state.recordBeingEdited as any;
            if (editingRecord?.dataKeyInput?.selectedCOA) {
                editingRecord.dataKeyInput.selectedCOA.delete(key);
            }
            return {
                ...state,
                datasetRecords: [...state.datasetRecords],
            };
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.REMOVE_UNUSED_DATA_KEY_COA_INPUTS.type: {
            if (state.recordBeingEdited?.dataKeyInput?.selectedCOA) {
                const coaInputMap = state.recordBeingEdited.dataKeyInput.selectedCOA;
                Array.from(coaInputMap.keys()).forEach(x => {
                    if (coaInputMap.get(x)?.trim() == '') coaInputMap.delete(x);
                });
            }
            return {
                ...state,
                datasetRecords: [...state.datasetRecords],
            };
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_ERRORS.type: {
            const {recordID, value} = action.payload;
            const records = [...state.datasetRecords];
            const selectedRecord = records.find(x => x.datasourceId == recordID);
            if (selectedRecord) {
                selectedRecord.errors = value;
            }
            return {
                ...state,
                datasetRecords: records,
            };
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.CLEAR_ERRORS.type: {
            const {recordID} = action.payload;
            const records = [...state.datasetRecords];
            const selectedRecord = records.find(x => x.datasourceId == recordID);
            if (selectedRecord) {
                selectedRecord.errors = undefined;
            }
            return {
                ...state,
                datasetRecords: records,
            };
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_FX_INPUTS.type: {
            // TODO: Add FX inputs logic if needed
            return {
                ...state,
                datasetRecords: [...state.datasetRecords],
            };
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_DATA_KEY_INPUT.type: {
            const {key, value} = action.payload;
            const editingRecord = state.recordBeingEdited as any;
            if (editingRecord?.dataKeyInput) {
                editingRecord.dataKeyInput[key] = value;
            }
            return {
                ...state,
                datasetRecords: [...state.datasetRecords],
                selectedCustomCoaValues: undefined,
            };
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_RECORD_ID_FOR_ACTION.type: {
            return {
                ...state,
                recordIdForAction: action.payload,
            }
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SHOW_EDIT_CONFIRM_MODAL.type: {
            return {
                ...state,
                showEditActionConfirmModal: true,
            }
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SHOW_DUPLICATE_CONFIRM_MODAL.type: {
            return {
                ...state,
                showDuplicateActionConfirmModal: true,
            }
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SHOW_DELETE_CONFIRM_MODAL.type: {
            return {
                ...state,
                showDeleteActionConfirmModal: true,
            }
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.DUPLICATE_RECORD.type: {
            const records = [...state.datasetRecords];
            const selectedRecordArr = records.filter(x => x.datasourceId == state.recordIdForAction);
            if (selectedRecordArr.length > 0) {
                const selectedRecord = selectedRecordArr[0];
                const selectedRecordCopy = {
                    ...selectedRecord,
                    datasourceId: String(state.recordIdCounter),
                    description: CONSTANTS.DUPLICATE_DATA_SOURCE_DESC_PREFIX + selectedRecord.description,
                    isNewRecord: true,
                    isEditing: true,
                    showDataKeySelection: true,
                    requestBalancePull: false,
                    balancePullInProgress: false,
                    balancePullingTask: undefined,
                    abandonedBalancePullRequests: [],
                    errors: undefined,
                    pullBalanceError: undefined,
                    balancePullStatus: "Not Started",
                    lastBalancePullId: undefined,
                    lastBalancePullDate: undefined,
                };
                if (selectedRecord.dataKeyInput != null) {
                    selectedRecordCopy.dataKeyInput = {...selectedRecord.dataKeyInput};
                    if (selectedRecord.dataKeyInput.selectedCOA != null) {
                        selectedRecordCopy.dataKeyInput.selectedCOA = new Map(selectedRecord.dataKeyInput.selectedCOA);
                    }
                }

                records.push(selectedRecordCopy);
                return {
                    ...state,
                    recordBeingEdited: selectedRecordCopy,
                    datasetRecords: records,
                    recordIdCounter: state.recordIdCounter + 1,
                    savedRecordId: undefined,
                }
            } else {
                return state;
            }
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.HIDE_DUPLICATE_CONFIRM_MODAL.type: {
            return {
                ...state,
                showDuplicateActionConfirmModal: false,
            }
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.HIDE_DELETE_CONFIRM_MODAL.type: {
            return {
                ...state,
                showDeleteActionConfirmModal: false,
            }
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.TOGGLE_CATEGORY_EXPANSION.type: {
            const editingRecord = (state.recordBeingEdited as any);
            if (editingRecord['expandedCategories'] == undefined) {
                editingRecord['expandedCategories'] = new Array<string>();
            }
            const expandedCategories = (editingRecord['expandedCategories'] as string[])
            if (expandedCategories.includes(action.payload)) {
                editingRecord['expandedCategories'] = expandedCategories.filter(x => x != action.payload);
            } else {
                expandedCategories.push(action.payload);
            }

            return {
                ...state,
            }
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.UPDATE_BALANCE_PULL_STATUS.type:
            const statusMap = action.payload as Map<string, string>;
            const updatedRecordsWithStatus = state.datasetRecords.map(record => {
                // Only update if this datasource is in the statusMap
                if (statusMap.has(record.datasourceId)) {
                    return {
                        ...record,
                        balancePullStatus: statusMap.get(record.datasourceId)!
                    };
                }
                // Otherwise, keep the existing record unchanged
                return record;
            });
            
            // Also update recordBeingEdited if it matches the updated record
            const updatedRecordBeingEditedWithStatus = state.recordBeingEdited?.datasourceId && statusMap.has(state.recordBeingEdited.datasourceId)
                ? updatedRecordsWithStatus.find(r => r.datasourceId === state.recordBeingEdited?.datasourceId)
                : state.recordBeingEdited;
            
            return {
                ...state,
                datasetRecords: updatedRecordsWithStatus,
                recordBeingEdited: updatedRecordBeingEditedWithStatus
            };

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.REFRESH_BALANCE_STATUS.type: {
            return {
                ...state,
                refreshBalanceStatusDatasourceId: action.payload
            };
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.UPDATE_LAST_BALANCE_PULL_ID.type: {
            const { datasourceId, lastBalancePullId } = action.payload;
            const updatedRecords = state.datasetRecords.map(record => 
                record.datasourceId === datasourceId 
                    ? { ...record, lastBalancePullId }
                    : record
            );
            // Also update recordBeingEdited if it matches the updated record
            const updatedRecordBeingEdited = state.recordBeingEdited?.datasourceId === datasourceId
                ? updatedRecords.find(r => r.datasourceId === datasourceId)
                : state.recordBeingEdited;
            
            return {
                ...state,
                datasetRecords: updatedRecords,
                recordBeingEdited: updatedRecordBeingEdited
            };
        }

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_ALLOCATION_SHEETS.type:
            return {
                ...state,
                allocationSheets: action.payload,
                filteredAllocationSheets: action.payload,
                allocationSheetsLoading: false,
            };

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_ALLOCATION_SHEETS_LOADING.type:
            return {
                ...state,
                allocationSheetsLoading: action.payload,
            };

        case MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.FILTER_ALLOCATION_SHEETS_BY_SEGMENT.type:
            // TODO: Need to remove this regex once the ENUM issue in the Allocation Sheet is resolved.
            const selectedSegment = action.payload?.toUpperCase().replace(/\s+/g, '_');
            const filteredSheets = selectedSegment 
                ? (state.allocationSheets || []).filter(sheet => sheet.segmentType === selectedSegment)
                : (state.allocationSheets || []);
            
            return {
                ...state,
                filteredAllocationSheets: filteredSheets,
            };

        default:
            return state;
    }
}

function getSelectedRecordAndParams(payload: any, records: MultiAllocationDataSourceRecord[], checkUnsaved: boolean = false) {
    const {recordID, value = null} = payload;
    let selectedRecord = null;
    if (recordID != null) {
        selectedRecord = records.find(x => x.datasourceId == recordID);
        if (selectedRecord == null && checkUnsaved != null && checkUnsaved) {
            selectedRecord = records.find(x => x.unsavedDatasourceId == recordID);
        }
    }
    return {selectedRecord: selectedRecord || null, value};
}
